/**
 * Estimating how often the loaded data actually changes.
 *
 * Why this exists: the zoom floor cannot be a constant. A window is only useful
 * if it can contain a meaningful number of *real* samples, and how much time
 * that takes is a property of the data, not of the chart. `timeRangeClamp`
 * turns the estimate produced here into a span floor; see
 * `minSpanForSampleIntervalMs`.
 */

/**
 * Minimum number of gaps required before we trust an estimate.
 *
 * With fewer than this, a single irregular gap (a logging outage, a tag that
 * only writes on change) dominates the median and we would rather fall back to
 * the documented default than act on noise.
 */
const MIN_GAPS_FOR_ESTIMATE = 4

/**
 * Median interval between consecutive samples, in milliseconds.
 *
 * Median rather than mean: PI histories are full of logging gaps (hours of
 * missing data around an outage) and duplicate-timestamp rows. A mean is
 * dragged upward by a single outage and would inflate the zoom floor; the
 * median reports the cadence of the typical sample.
 *
 * `timestamps` may be unsorted and may contain duplicates. Returns `null` when
 * there is not enough evidence to estimate, so callers can fall back explicitly
 * rather than act on a fabricated number.
 */
export function estimateSampleIntervalMs(timestamps: number[]): number | null {
  const sorted = timestamps
    .filter((timestamp) => Number.isFinite(timestamp))
    .sort((left, right) => left - right)

  const gaps: number[] = []
  for (let index = 1; index < sorted.length; index++) {
    const gap = sorted[index] - sorted[index - 1]
    // Zero gaps are duplicate rows (two sensors sampled at the same instant),
    // not a cadence of zero.
    if (gap > 0) gaps.push(gap)
  }

  if (gaps.length < MIN_GAPS_FOR_ESTIMATE) return null

  gaps.sort((left, right) => left - right)
  const middle = Math.floor(gaps.length / 2)

  return gaps.length % 2 === 0
    ? (gaps[middle - 1] + gaps[middle]) / 2
    : gaps[middle]
}
